import { useState } from "react";
import { useTranslation } from "react-i18next";

import RenderIf from "common/components/RenderIf";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import useFlowsActions from "../../../../hooks/useFlowsActions";
import useValidateFlow from "../../../../hooks/useValidateFlow";
import CampaignFlow from "../../CampaignFlow";
import FlowTypeLabel from "../../FlowTypeLabel";
import FlowActions from "./FlowActions";
import FlowSelection from "./FlowSelection";

import type { CampaignFlow as CampaignFlowType } from "../../../../types";

interface CampaignFlowsProps {
  flows: CampaignFlowType[];
  targetCount: number;
  isConnect: boolean;
}

export default function PrimaryFlows({
  flows,
  targetCount,
  isConnect,
}: CampaignFlowsProps) {
  const isDesktop = useTwBreakpoint("lg");
  const { t } = useTranslation("campaigns", { keyPrefix: "flowTypes" });

  const [selectedFlowIndex, setSelectedFlowIndex] = useState(0);
  const safeIndex = Math.max(0, Math.min(selectedFlowIndex, flows.length - 1));
  const selectedFlow = flows[safeIndex];

  const { isFlowError, resetFlowError } = useValidateFlow(selectedFlow.id);
  const { deleteFlow } = useFlowsActions(flows, setSelectedFlowIndex);

  return (
    <article className="rounded-3xl border border-black-200 p-3">
      {/* Heading */}
      <section className="flex gap-x-3">
        <FlowTypeLabel type={flows[0].type} />

        <div>
          <h2 className="mb-1 text-lg-bold">
            {isConnect ? "Connect" : "Message"} Flow
          </h2>

          <p className="text-black-500">{t(`${flows[0].type}_Description`)}</p>
        </div>
      </section>

      {/* Tab Selection */}
      <FlowSelection
        flows={flows}
        selectedIndex={selectedFlowIndex}
        setSelectedIndex={setSelectedFlowIndex}
        targetCount={targetCount}
      />

      <RenderIf condition={!isDesktop}>
        <div className="mb-5 flex items-center">
          <h3 className="line-clamp-1 flex-1 gap-2 text-lg-bold">
            {selectedFlow.name}
          </h3>

          <FlowActions
            flow={selectedFlow}
            variant="secondary-black"
            deleteFlow={flows.length === 2 && deleteFlow}
          />
        </div>
      </RenderIf>

      {/* Selected Flow */}
      <CampaignFlow
        flow={selectedFlow}
        isFlowError={isFlowError}
        resetFlowError={resetFlowError}
      />
    </article>
  );
}
